(function () {
  'use strict';

  var DATA = window.SystemBuilderData;
  if (!DATA) return;

  var productCache = {};
  var productsLoaded = false;
  var currentStep = 1;
  var maxStepReached = 1;

  var state = {};
  DATA.categories.forEach(function (cat) {
    state[cat.id] = { state: 'skip', sizeIdx: cat.defaultSizeIdx || 0, backwashing: false };
  });

  // Raw water pH below 6.0 flips the install order: the acid neutralizer
  // goes BEFORE the iron filter (air-injection iron filters need corrected
  // pH to oxidize iron). Toggled on the AN sizing card, pre-filled from the
  // saved water profile.
  var lowPh = false;

  // ---- Cross-page water profile + builder state persistence ----
  // The water profile is the same object the collection-page sizer writes
  // when a customer enters their iron / pH / household. Reading it here
  // lets the wizard pre-select the right system + size on first visit.
  // The builder state itself is persisted separately so that mid-wizard
  // selections survive a page reload or accidental tab close.

  var WATER_PROFILE_KEY = 'maw_water_profile';
  var BUILDER_STATE_KEY = 'maw_builder_state';

  function loadJSON(key) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return null;
      var v = JSON.parse(raw);
      return (v && typeof v === 'object') ? v : null;
    } catch (e) { return null; }
  }
  function saveJSON(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) {}
  }
  function clearKey(key) {
    try { localStorage.removeItem(key); } catch (e) {}
  }

  function saveBuilderState() {
    var snapshot = {};
    DATA.categories.forEach(function (cat) {
      var s = state[cat.id];
      if (!s || s.state === 'skip') return;
      snapshot[cat.id] = { state: s.state, sizeIdx: s.sizeIdx, backwashing: !!s.backwashing };
    });
    if (Object.keys(snapshot).length === 0) {
      clearKey(BUILDER_STATE_KEY);
    } else {
      saveJSON(BUILDER_STATE_KEY, { categories: snapshot, lowPh: lowPh, updated_at: new Date().toISOString() });
    }
  }

  function applyBuilderStateSnapshot(snapshot) {
    if (!snapshot || !snapshot.categories) return false;
    lowPh = !!snapshot.lowPh;
    var found = false;
    Object.keys(snapshot.categories).forEach(function (catId) {
      if (state[catId]) {
        var v = snapshot.categories[catId];
        state[catId] = {
          state: v.state || 'skip',
          sizeIdx: typeof v.sizeIdx === 'number' ? v.sizeIdx : 0,
          backwashing: !!v.backwashing
        };
        if (state[catId].state !== 'skip') found = true;
      }
    });
    return found;
  }

  // Map household -> water softener sizeIdx (32k=0, 48k=1, 64k=2). Source of truth:
  // system-builder-data.js softener sizes (bestFor 2-5 / 2-8 / 2-8). 1-2 people -> 32k,
  // 3-5 -> 48k, 6+ -> 64k matches Aidan's call recommendations.
  function householdToSoftenerIdx(household) {
    if (household == null) return null;
    if (household <= 2) return 0;
    if (household <= 5) return 1;
    return 2;
  }

  // Map iron ppm + household -> iron filter sizeIdx (1.5=0, 2.0=1, 2.5=2). Same rules
  // as the collection sizer: smallest tank that fits both iron and household, with a
  // household-driven upsize for >=4 people.
  function ironToSizeIdx(iron_ppm, household) {
    if (iron_ppm == null && household == null) return null;
    var iron = iron_ppm == null ? 0 : iron_ppm;
    var idx;
    if (iron <= 15) idx = 0;
    else if (iron <= 20) idx = 1;
    else idx = 2;
    if (household != null && household >= 4 && idx < 1) idx = 1;
    if (household != null && household >= 6 && idx < 2) idx = 2;
    return idx;
  }

  // Apply a saved water profile to the wizard state. We only flip a category
  // to 'need' if the profile clearly indicates the customer needs it. We
  // never overwrite 'have' (already-owned) selections.
  function applyWaterProfileToState(profile) {
    if (!profile) return false;
    var changed = false;

    // Iron filter -- if iron is reported at any level above 0.3 ppm, flag as needed.
    if (profile.iron_ppm != null && profile.iron_ppm > 0.3 && state.iron_filter && state.iron_filter.state !== 'have') {
      var ironIdx = ironToSizeIdx(profile.iron_ppm, profile.household);
      if (ironIdx != null) {
        state.iron_filter.state = 'need';
        state.iron_filter.sizeIdx = ironIdx;
        changed = true;
      }
    }

    // Acid neutralizer -- only flag as needed if pH < 6.8 (the same threshold the
    // collection sizer uses for the "add an AN" warning, sourced from calls #371/#73).
    if (profile.ph != null && profile.ph < 6.8 && state.acid_neutralizer && state.acid_neutralizer.state !== 'have') {
      state.acid_neutralizer.state = 'need';
      // Default to non-backwashing unless household >= 4 (backwashing makes sense for higher use).
      var hh = profile.household;
      var anCat = DATA.categories.filter(function (c) { return c.id === 'acid_neutralizer'; })[0];
      if (anCat) {
        var preferBw = (hh != null && hh >= 4);
        state.acid_neutralizer.backwashing = preferBw;
        state.acid_neutralizer.sizeIdx = preferBw
          ? (anCat.defaultBackwashingSizeIdx || 0)
          : (anCat.defaultSizeIdx || 0);
      }
      changed = true;
    }

    // Raw pH below 6.0 -> the neutralizer installs before the iron filter.
    if (profile.ph != null && profile.ph < 6) {
      lowPh = true;
      changed = true;
    }

    // Water softener -- if hardness is reported at >= 7 gpg, flag as needed and size by household.
    if (profile.hardness_gpg != null && profile.hardness_gpg >= 7 && state.water_softener && state.water_softener.state !== 'have') {
      state.water_softener.state = 'need';
      var sIdx = householdToSoftenerIdx(profile.household);
      if (sIdx != null) state.water_softener.sizeIdx = sIdx;
      changed = true;
    }

    return changed;
  }

  // ---- Helpers ----

  function getActiveSizes(cat) {
    var s = state[cat.id];
    if (s.backwashing && cat.backwashingSizes) return cat.backwashingSizes;
    return cat.sizes;
  }

  function getAllHandles() {
    var handles = [];
    DATA.categories.forEach(function (cat) {
      cat.sizes.forEach(function (sz) {
        if (handles.indexOf(sz.handle) === -1) handles.push(sz.handle);
      });
      if (cat.backwashingSizes) {
        cat.backwashingSizes.forEach(function (sz) {
          if (handles.indexOf(sz.handle) === -1) handles.push(sz.handle);
        });
      }
    });
    return handles;
  }

  function shopifyImageSize(url, size) {
    if (!url) return '';
    return url.replace(/(\.[a-z]+)(\?|$)/, '_' + size + '$1$2');
  }

  function fetchAllProducts(callback) {
    var handles = getAllHandles();
    if (DATA.wellTankHandle) handles.push(DATA.wellTankHandle);
    var pending = handles.length;
    if (pending === 0) { callback(); return; }

    handles.forEach(function (handle) {
      fetch('/products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (p) {
          productCache[handle] = {
            title: p.title,
            price: p.price,
            image: p.featured_image,
            variantId: p.variants[0].id,
            url: p.url
          };
        })
        .catch(function () {})
        .finally(function () {
          pending--;
          if (pending === 0) { productsLoaded = true; callback(); }
        });
    });
  }

  function getProduct(handle) { return productCache[handle] || null; }

  function formatPrice(cents) {
    var dollars = Math.floor(cents / 100);
    var remainder = cents % 100;
    return '$' + dollars.toLocaleString('en-US') + (remainder > 0 ? '.' + (remainder < 10 ? '0' : '') + remainder : '');
  }

  function inchesToFeetLabel(inches) {
    var ft = Math.floor(inches / 12);
    var inRem = Math.round(inches % 12);
    if (ft === 0) return inRem + '"';
    if (inRem === 0) return ft + "'";
    return ft + "' " + inRem + '"';
  }

  function getSizePrice(size) {
    var p = getProduct(size.handle);
    return p ? p.price : 0;
  }

  function getSizeImage(size, imgSize) {
    var p = getProduct(size.handle);
    if (!p || !p.image) return '';
    return shopifyImageSize(p.image, imgSize || '200x200');
  }

  // Categories in installation order. Default order comes from the data file
  // (sediment -> iron -> AN -> softener -> carbon -> UV). When raw pH is
  // below 6.0 the acid neutralizer moves BEFORE the iron filter.
  function orderedCategories() {
    var cats = DATA.categories.slice();
    if (!lowPh) return cats;
    var ironIdx = -1, anIdx = -1;
    cats.forEach(function (c, i) {
      if (c.id === 'iron_filter') ironIdx = i;
      if (c.id === 'acid_neutralizer') anIdx = i;
    });
    if (ironIdx !== -1 && anIdx !== -1 && anIdx > ironIdx) {
      var an = cats.splice(anIdx, 1)[0];
      cats.splice(ironIdx, 0, an);
    }
    return cats;
  }

  // True when the low-pH order swap is actually in play (both systems present).
  function lowPhSwapActive() {
    return lowPh &&
      state.iron_filter && state.iron_filter.state !== 'skip' &&
      state.acid_neutralizer && state.acid_neutralizer.state !== 'skip';
  }

  function getActiveCats() {
    return orderedCategories().filter(function (cat) { return state[cat.id].state !== 'skip'; });
  }

  function getNewCats() {
    return orderedCategories().filter(function (cat) { return state[cat.id].state === 'need'; });
  }

  // ---- Step Navigation ----

  function goToStep(step) {
    currentStep = step;
    if (step > maxStepReached) maxStepReached = step;
    for (var i = 1; i <= 3; i++) {
      var el = document.getElementById('sb-step-' + i);
      if (el) el.style.display = (i === step) ? 'block' : 'none';
    }
    updateProgress();
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (step === 1) renderStep1();
    if (step === 2) renderStep2();
    if (step === 3) { renderStep3(); updateUrlState(); }
  }

  function updateProgress() {
    var steps = document.querySelectorAll('#sb-progress .sb-progress-step');
    var lines = document.querySelectorAll('#sb-progress .sb-progress-line');
    for (var i = 0; i < steps.length; i++) {
      var stepNum = i + 1;
      steps[i].className = 'sb-progress-step';
      if (stepNum === currentStep) steps[i].classList.add('sb-step-active');
      else if (stepNum < currentStep) steps[i].classList.add('sb-step-done');
      if (stepNum !== currentStep && canReachStep(stepNum)) {
        steps[i].classList.add('sb-step-tappable');
      }
    }
    for (var j = 0; j < lines.length; j++) {
      lines[j].className = 'sb-progress-line' + (j + 1 < currentStep ? ' sb-line-done' : '');
    }
  }

  function canReachStep(step) {
    if (step === 1) return true;
    if (step > maxStepReached) return false;
    return getActiveCats().length > 0;
  }

  var STEP_LABELS_FULL = ['Equipment', 'Sizing', 'Your Install Plan'];
  var STEP_LABELS_SHORT = ['Equipment', 'Sizing', 'Plan'];

  function initProgressLabels() {
    var progress = document.getElementById('sb-progress');
    if (!progress) return;

    function relabel() {
      var labels = progress.querySelectorAll('.sb-step-label');
      var set = window.innerWidth > 600 ? STEP_LABELS_FULL : STEP_LABELS_SHORT;
      for (var i = 0; i < labels.length; i++) {
        if (set[i]) labels[i].textContent = set[i];
      }
    }

    relabel();
    window.addEventListener('resize', relabel);
  }

  function initProgressNav() {
    var steps = document.querySelectorAll('#sb-progress .sb-progress-step');
    for (var i = 0; i < steps.length; i++) {
      (function (idx) {
        steps[idx].addEventListener('click', function () {
          var targetStep = idx + 1;
          if (targetStep !== currentStep && canReachStep(targetStep)) {
            goToStep(targetStep);
          }
        });
      })(i);
    }
  }

  // ---- Step 1: Selection ----

  function renderStep1() {
    var container = document.getElementById('sb-step1-list');
    if (!container) return;

    var html = '';
    DATA.categories.forEach(function (cat) {
      var s = state[cat.id];
      var active = s.state === 'need' || s.state === 'have';
      var img = getSizeImage(cat.sizes[0], '200x200');
      html += '<div class="sb-pick-card' + (active ? ' sb-picked' : '') + '" data-cat="' + cat.id + '" data-state="' + s.state + '">';
      html += '<div class="sb-pick-check">' + (active ? '&#10003;' : '') + '</div>';
      if (img) html += '<img class="sb-pick-img" src="' + img + '" alt="' + cat.name + '" width="100" height="100" loading="lazy">';
      html += '<div class="sb-pick-name">' + cat.name + '</div>';
      html += '<div class="sb-pick-desc">' + cat.description + '</div>';
      html += '<div class="sb-toggle-row">';
      html += '<label class="sb-need-toggle' + (s.state === 'need' ? ' sb-need-checked' : '') + '" data-cat="' + cat.id + '">I need this</label>';
      html += '<label class="sb-own-toggle' + (s.state === 'have' ? ' sb-own-checked' : '') + '" data-cat="' + cat.id + '">I own this</label>';
      html += '</div>';
      html += '</div>';
    });

    container.innerHTML = html;

    var cards = container.querySelectorAll('.sb-pick-card');
    for (var i = 0; i < cards.length; i++) {
      cards[i].addEventListener('click', handleCardTap);
    }

    var needs = container.querySelectorAll('.sb-need-toggle');
    for (var j = 0; j < needs.length; j++) {
      needs[j].addEventListener('click', handleNeedToggle);
    }

    var owns = container.querySelectorAll('.sb-own-toggle');
    for (var k = 0; k < owns.length; k++) {
      owns[k].addEventListener('click', handleOwnToggle);
    }

    updateStep1Next();
  }

  function handleCardTap(e) {
    if (e.target.closest('.sb-own-toggle') || e.target.closest('.sb-need-toggle')) return;
    var catId = e.currentTarget.getAttribute('data-cat');
    var s = state[catId];
    if (s.state === 'need' || s.state === 'have') {
      s.state = 'skip';
    } else {
      s.state = 'need';
    }
    saveBuilderState();
    renderStep1();
  }

  function handleNeedToggle(e) {
    e.stopPropagation();
    var catId = e.currentTarget.getAttribute('data-cat');
    state[catId].state = state[catId].state === 'need' ? 'skip' : 'need';
    saveBuilderState();
    renderStep1();
  }

  function handleOwnToggle(e) {
    e.stopPropagation();
    var catId = e.currentTarget.getAttribute('data-cat');
    state[catId].state = state[catId].state === 'have' ? 'skip' : 'have';
    saveBuilderState();
    renderStep1();
  }

  function updateStep1Next() {
    var btn = document.getElementById('sb-next-1');
    if (!btn) return;
    var hasActive = getActiveCats().length > 0;
    btn.disabled = !hasActive;
    updateProgress();
  }

  // ---- Step 2: Sizing ----

  function renderStep2() {
    var container = document.getElementById('sb-step2-list');
    if (!container) return;

    var activeCats = getActiveCats();
    var html = '';

    activeCats.forEach(function (cat) {
      var s = state[cat.id];
      var sizes = getActiveSizes(cat);
      var size = sizes[s.sizeIdx];
      var img = getSizeImage(size, '160x160');
      var price = getSizePrice(size);

      html += '<div class="sb-size-card" data-state="' + s.state + '">';

      if (img) {
        html += '<img class="sb-size-img" src="' + img + '" alt="' + cat.name + '" width="80" height="80" loading="lazy">';
      }

      html += '<div class="sb-size-body">';
      html += '<div class="sb-size-name">' + cat.name;
      if (s.state === 'need') html += ' <span class="sb-size-badge sb-badge-new">New</span>';
      if (s.state === 'have') html += ' <span class="sb-size-badge sb-badge-owned">Owned</span>';
      html += '</div>';

      if (s.state === 'have') {
        html += '<p class="sb-size-owned-note">Already installed. Shown in your diagram for reference.</p>';
      } else {
        if (sizes.length > 1) {
          html += '<div class="sb-size-options" data-cat="' + cat.id + '">';
          sizes.forEach(function (sz, idx) {
            var szPrice = getSizePrice(sz);
            var active = idx === s.sizeIdx;
            html += '<button type="button" class="sb-size-opt' + (active ? ' sb-size-opt-active' : '') + '" data-cat="' + cat.id + '" data-idx="' + idx + '">';
            html += '<span class="sb-size-opt-label">' + sz.label + '</span>';
            if (szPrice) html += '<span class="sb-size-opt-price">' + formatPrice(szPrice) + '</span>';
            html += '</button>';
          });
          html += '</div>';
        } else if (price) {
          html += '<div class="sb-size-price">' + formatPrice(price) + '</div>';
        }

        if (cat.canBackwash) {
          html += '<div class="sb-backwash-row">';
          html += '<button type="button" class="sb-bw-opt' + (!s.backwashing ? ' sb-bw-opt-active' : '') + '" data-cat="' + cat.id + '" data-bw="0">Non-Backwashing</button>';
          html += '<button type="button" class="sb-bw-opt' + (s.backwashing ? ' sb-bw-opt-active' : '') + '" data-cat="' + cat.id + '" data-bw="1">Backwashing</button>';
          html += '</div>';
        }
      }

      // Low-pH question: only matters when both the AN and an iron filter
      // are in the plan, because it decides which one installs first.
      if (cat.id === 'acid_neutralizer' && state.iron_filter && state.iron_filter.state !== 'skip') {
        html += '<div class="sb-ph-question">';
        html += '<div class="sb-ph-question-label">Is your raw water pH below 6.0?</div>';
        html += '<div class="sb-backwash-row">';
        html += '<button type="button" class="sb-bw-opt sb-ph-opt' + (!lowPh ? ' sb-bw-opt-active' : '') + '" data-ph="0">No / Not sure</button>';
        html += '<button type="button" class="sb-bw-opt sb-ph-opt' + (lowPh ? ' sb-bw-opt-active' : '') + '" data-ph="1">Yes, below 6.0</button>';
        html += '</div>';
        if (lowPh) {
          html += '<p class="sb-ph-note">Your neutralizer will be placed <strong>before</strong> the iron filter in your install plan. Water this acidic must be corrected before the iron filter can remove iron properly.</p>';
        } else {
          html += '<p class="sb-ph-note">Below pH 6.0, the neutralizer installs before the iron filter instead of after it. Check your water test if you\'re not sure.</p>';
        }
        html += '</div>';
      }

      html += '</div></div>';
    });

    container.innerHTML = html;

    var opts = container.querySelectorAll('.sb-size-opt');
    for (var j = 0; j < opts.length; j++) {
      opts[j].addEventListener('click', function (e) {
        var catId = e.currentTarget.getAttribute('data-cat');
        state[catId].sizeIdx = parseInt(e.currentTarget.getAttribute('data-idx'), 10);
        saveBuilderState();
        renderStep2();
      });
    }

    var phBtns = container.querySelectorAll('.sb-ph-opt');
    for (var m = 0; m < phBtns.length; m++) {
      phBtns[m].addEventListener('click', function (e) {
        var newLowPh = e.currentTarget.getAttribute('data-ph') === '1';
        if (lowPh !== newLowPh) {
          lowPh = newLowPh;
          saveBuilderState();
          renderStep2();
        }
      });
    }

    var bwBtns = container.querySelectorAll('.sb-bw-opt:not(.sb-ph-opt)');
    for (var k = 0; k < bwBtns.length; k++) {
      bwBtns[k].addEventListener('click', function (e) {
        var catId = e.currentTarget.getAttribute('data-cat');
        var bw = e.currentTarget.getAttribute('data-bw') === '1';
        if (state[catId].backwashing !== bw) {
          state[catId].backwashing = bw;
          var cat = DATA.categories.filter(function (c) { return c.id === catId; })[0];
          state[catId].sizeIdx = bw ? (cat.defaultBackwashingSizeIdx || 0) : (cat.defaultSizeIdx || 0);
          saveBuilderState();
          renderStep2();
        }
      });
    }

    updateScrollHint();
  }

  function updateScrollHint() {
    var hint = document.getElementById('sb-scroll-hint');
    if (!hint) return;
    var lastCard = document.querySelector('#sb-step2-list .sb-size-card:last-child');
    if (!lastCard) { hint.classList.add('sb-hint-hidden'); return; }
    var rect = lastCard.getBoundingClientRect();
    if (rect.bottom > window.innerHeight - 100) {
      hint.classList.remove('sb-hint-hidden');
    } else {
      hint.classList.add('sb-hint-hidden');
    }
  }

  // ---- Step 3: Plan ----

  var INSTALL_REQS = {
    sediment:            { outletShort: null,   drain: null,        bypass: 'Shut-offs', note: 'Replace cartridge every 6-12 months' },
    iron_filter:         { outletShort: '120V', drain: '1/2" line', bypass: 'Included',  note: null },
    acid_neutralizer:    { outletShort: null,   drain: null,        bypass: 'Included',  note: 'Upflow design, no backwash needed' },
    acid_neutralizer_bw: { outletShort: '120V', drain: '1/2" line', bypass: 'Included',  note: null },
    water_softener:      { outletShort: '120V', drain: '1/2" line', bypass: 'Included',  note: 'Keep salt tank within 2 ft' },
    carbon_filter:       { outletShort: null,   drain: null,        bypass: 'Included',  note: 'Upflow design, no backwash needed' },
    carbon_filter_bw:    { outletShort: '120V', drain: '1/2" line', bypass: 'Included',  note: null },
    uv_system:           { outletShort: 'GFCI', drain: null,        bypass: 'Shut-offs', note: 'Install last. Use copper fittings (not PEX).' }
  };

  function renderStep3() {
    renderInstallDiagram();
    renderInstallReqs();
    renderInstallInstructions();
    renderSpace();
    renderCart();
  }

  function renderInstallDiagram() {
    if (window.SystemBuilderDiagram) {
      var container = document.getElementById('sb-install-diagram');
      window.SystemBuilderDiagram.render(container, { categories: orderedCategories() }, state);
    }
  }

  function saveDiagram() {
    var svgEl = document.querySelector('#sb-install-diagram svg');
    if (!svgEl) return;

    var scale = 3;
    var svgData = new XMLSerializer().serializeToString(svgEl);
    var vb = svgEl.getAttribute('viewBox').split(' ');
    var w = parseFloat(vb[2]);
    var h = parseFloat(vb[3]);

    var canvas = document.createElement('canvas');
    canvas.width = w * scale;
    canvas.height = h * scale;
    var ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    var img = new Image();
    var blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    var url = URL.createObjectURL(blob);

    img.onload = function () {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);

      canvas.toBlob(function (pngBlob) {
        if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
          var file = new File([pngBlob], 'installation-diagram.png', { type: 'image/png' });
          navigator.share({ files: [file], title: 'Installation Diagram' }).catch(function () {
            downloadBlob(pngBlob);
          });
        } else {
          downloadBlob(pngBlob);
        }
      }, 'image/png');
    };
    img.src = url;

    function downloadBlob(b) {
      var a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = 'installation-diagram.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    }
  }

  function renderInstallReqs() {
    var grid = document.getElementById('sb-reqs-grid');
    if (!grid) return;

    var activeCats = getActiveCats();
    var html = '';

    activeCats.forEach(function (cat) {
      var s = state[cat.id];
      var key = cat.id;
      if (s.backwashing && INSTALL_REQS[key + '_bw']) key = key + '_bw';
      var req = INSTALL_REQS[key];
      if (!req) return;

      var sizes = getActiveSizes(cat);
      var sz = sizes[s.sizeIdx] || sizes[0];
      var img = getSizeImage(sz, '120x120');

      html += '<div class="sb-req-card">';
      if (img) html += '<img class="sb-req-card-img" src="' + img + '" alt="' + cat.name + '" width="60" height="60" loading="lazy">';
      html += '<div class="sb-req-card-content">';
      html += '<div class="sb-req-card-name">' + cat.name;
      if (s.backwashing) html += ' <span class="sb-req-bw-tag">BW</span>';
      html += '</div>';
      html += '<div class="sb-req-card-items">';

      if (req.outletShort === 'GFCI') {
        html += '<div class="sb-req-item sb-req-item-warn"><span class="sb-req-item-icon">&#9889;</span><span class="sb-req-item-label">GFCI 120V Outlet</span></div>';
      } else if (req.outletShort) {
        html += '<div class="sb-req-item sb-req-item-need"><span class="sb-req-item-icon">&#9889;</span><span class="sb-req-item-label">120V Outlet</span></div>';
      } else {
        html += '<div class="sb-req-item sb-req-item-none"><span class="sb-req-item-icon">&#9889;</span><span class="sb-req-item-label">No outlet needed</span></div>';
      }

      if (req.drain) {
        html += '<div class="sb-req-item sb-req-item-need"><span class="sb-req-item-icon">&#128167;</span><span class="sb-req-item-label">Drain (' + req.drain + ')</span></div>';
      } else {
        html += '<div class="sb-req-item sb-req-item-none"><span class="sb-req-item-icon">&#128167;</span><span class="sb-req-item-label">No drain needed</span></div>';
      }

      if (req.bypass === 'Included') {
        html += '<div class="sb-req-item sb-req-item-good"><span class="sb-req-item-icon">&#10003;</span><span class="sb-req-item-label">Bypass included</span></div>';
      } else {
        html += '<div class="sb-req-item"><span class="sb-req-item-icon">&#10003;</span><span class="sb-req-item-label">Bypass: ' + req.bypass + '</span></div>';
      }

      html += '</div>';

      if (req.note) {
        html += '<div class="sb-req-card-note">' + req.note + '</div>';
      }

      html += '</div></div>';
    });

    grid.innerHTML = html;
  }

  function parseTimeRange(str) {
    if (!str) return [0, 0];
    var nums = str.match(/[\d.]+/g);
    if (!nums || nums.length === 0) return [0, 0];
    var lo = parseFloat(nums[0]);
    var hi = nums.length > 1 ? parseFloat(nums[1]) : lo;
    if (str.indexOf('minute') !== -1) { lo /= 60; hi /= 60; }
    return [lo, hi];
  }

  function formatHourRange(lo, hi) {
    lo = Math.round(lo * 2) / 2;
    hi = Math.round(hi * 2) / 2;
    if (lo < 1 && hi < 1) {
      return 'Under 1 hour';
    }
    function fmt(v) {
      if (v === 1) return '1 hour';
      if (v % 1 === 0) return v + ' hours';
      return v + ' hours';
    }
    if (lo === hi) return fmt(lo);
    return fmt(lo) + ' to ' + fmt(hi);
  }

  function estimateTotalTime(cats) {
    var totalLo = 0, totalHi = 0;
    var newCats = cats.filter(function (c) { return state[c.id].state === 'need'; });
    newCats.forEach(function (cat, idx) {
      var range = parseTimeRange(cat.installTime);
      if (idx === 0) {
        totalLo += range[0];
        totalHi += range[1];
      } else {
        totalLo += range[0] * 0.6;
        totalHi += range[1] * 0.6;
      }
    });
    return formatHourRange(totalLo, totalHi);
  }

  function renderInstallInstructions() {
    var container = document.getElementById('sb-install-instructions');
    if (!container) return;

    var activeCats = getActiveCats();
    if (activeCats.length === 0) {
      container.innerHTML = '';
      return;
    }

    var timeEstimate = estimateTotalTime(activeCats);
    var html = '<div class="sb-install-time-estimate"><span class="sb-install-time-icon">&#9200;</span> Estimated install time: <strong>' + timeEstimate + '</strong></div>';

    activeCats.forEach(function (cat, idx) {
      var s = state[cat.id];
      var isOwned = s.state === 'have';

      var steps = cat.installSteps || [];
      var notes = cat.installNotes || '';
      if (s.backwashing && cat.installStepsBackwashing) {
        steps = cat.installStepsBackwashing;
      }
      if (s.backwashing && cat.installNotesBackwashing) {
        notes = cat.installNotesBackwashing;
      }

      // Low-pH order swap: replace the positioning step (always step 1)
      // for the AN and iron filter so the text matches the diagram.
      if (lowPhSwapActive() && cat.positionStepLowPh && steps.length > 0) {
        steps = [cat.positionStepLowPh].concat(steps.slice(1));
      }

      if (steps.length === 0) return;

      var sizes = getActiveSizes(cat);
      var sz = sizes[s.sizeIdx] || sizes[0];
      var accImg = getSizeImage(sz, '80x80');

      var open = idx === 0 ? ' sb-accordion-open' : '';
      html += '<div class="sb-accordion' + open + '" data-accordion="' + cat.id + '">';
      html += '<button type="button" class="sb-accordion-trigger">';
      if (accImg) html += '<img class="sb-accordion-img" src="' + accImg + '" alt="' + cat.name + '" width="40" height="40" loading="lazy">';
      html += '<span class="sb-accordion-title">' + cat.name;
      if (isOwned) html += ' <span class="sb-accordion-owned">Already Owned</span>';
      if (s.backwashing) html += ' <span class="sb-accordion-bw">Backwashing</span>';
      html += '</span>';
      html += '<span class="sb-accordion-arrow">&#9662;</span>';
      html += '</button>';
      html += '<div class="sb-accordion-body">';

      if (isOwned) {
        html += '<p class="sb-install-owned-note">You marked this as already owned. These instructions are for reference if your plumber needs them.</p>';
      }

      html += '<ol class="sb-install-steps">';
      steps.forEach(function (step) {
        html += '<li>' + step + '</li>';
      });
      html += '</ol>';

      if (notes) {
        html += '<p class="sb-install-note"><strong>Note:</strong> ' + notes + '</p>';
      }

      html += '</div></div>';
    });

    container.innerHTML = html;

    var triggers = container.querySelectorAll('.sb-accordion-trigger');
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].addEventListener('click', function () {
        var acc = this.parentElement;
        acc.classList.toggle('sb-accordion-open');
      });
    }
  }

  function renderSpace() {
    var panel = document.getElementById('sb-space-panel');
    if (!panel) return;

    var newCats = getNewCats();
    if (newCats.length === 0) { panel.style.display = 'none'; return; }
    panel.style.display = 'block';

    var totalWidth = 0, maxHeight = 0, maxDepth = 0, hasSoftener = false;

    newCats.forEach(function (cat, idx) {
      var sizes = getActiveSizes(cat);
      var size = sizes[state[cat.id].sizeIdx];
      var w = size.tankDiaIn || size.widthIn || 10;
      if (cat.hasBrineTank) w += DATA.clearanceBetweenIn + cat.brineTankWidthIn;
      totalWidth += w;
      if (idx > 0) totalWidth += DATA.clearanceBetweenIn;
      var h = (size.heightIn || 60) + DATA.overheadClearanceIn;
      if (h > maxHeight) maxHeight = h;
      var d = size.depthIn || size.tankDiaIn || 12;
      if (cat.hasBrineTank && cat.brineTankDepthIn > d) d = cat.brineTankDepthIn;
      if (d > maxDepth) maxDepth = d;
      if (cat.hasBrineTank) hasSoftener = true;
    });

    document.getElementById('sb-space-width').textContent = inchesToFeetLabel(totalWidth);
    document.getElementById('sb-space-height').textContent = inchesToFeetLabel(maxHeight);
    document.getElementById('sb-space-depth').textContent = inchesToFeetLabel(maxDepth);

    var note = 'You need about ' + inchesToFeetLabel(totalWidth) + ' of wall space';
    if (hasSoftener) note += ' (includes 18" for the salt/brine tank)';
    note += '. Allow ' + inchesToFeetLabel(maxHeight) + ' of height and ' + inchesToFeetLabel(maxDepth) + ' clearance from the wall.';
    document.getElementById('sb-space-note').textContent = note;
  }

  function renderCart() {
    var section = document.getElementById('sb-cart-section');
    var itemsEl = document.getElementById('sb-cart-items');
    var totalEl = document.getElementById('sb-total-price');
    if (!section) return;

    var newCats = getNewCats();
    if (newCats.length === 0) { section.style.display = 'none'; return; }
    section.style.display = 'block';

    var total = 0;
    var html = '';

    newCats.forEach(function (cat) {
      var sizes = getActiveSizes(cat);
      var size = sizes[state[cat.id].sizeIdx];
      var price = getSizePrice(size);
      var img = getSizeImage(size, '96x96');
      total += price;

      html += '<div class="sb-cart-line">';
      if (img) html += '<img class="sb-cart-line-img" src="' + img + '" alt="" width="48" height="48" loading="lazy">';
      html += '<span class="sb-cart-line-name">' + cat.name + ' <span class="sb-cart-line-size">' + size.label + '</span></span>';
      html += '<span class="sb-cart-line-price">' + (price ? formatPrice(price) : '') + '</span>';
      html += '</div>';
    });

    itemsEl.innerHTML = html;
    totalEl.textContent = total ? formatPrice(total) : '$0';
  }

  // ---- Add to Cart ----

  function handleAddToCart() {
    var btn = document.getElementById('sb-add-to-cart');
    if (!btn || btn.disabled) return;

    var items = [];
    DATA.categories.forEach(function (cat) {
      if (state[cat.id].state !== 'need') return;
      var sizes = getActiveSizes(cat);
      var size = sizes[state[cat.id].sizeIdx];
      var p = getProduct(size.handle);
      if (p && p.variantId) items.push({ id: p.variantId, quantity: 1 });
    });

    if (items.length === 0) return;

    btn.disabled = true;
    btn.classList.add('sb-loading');
    btn.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (resp) {
        if (!resp.ok) throw new Error('Cart error');
        return resp.json();
      })
      .then(function () {
        btn.classList.remove('sb-loading');
        btn.textContent = 'Added!';
        var drawerToggle = document.querySelector('[data-cart-drawer-toggle]');
        if (drawerToggle) drawerToggle.click();
        setTimeout(function () {
          btn.disabled = false;
          btn.textContent = 'Add All to Cart';
        }, 2000);
      })
      .catch(function () {
        btn.disabled = false;
        btn.classList.remove('sb-loading');
        btn.textContent = 'Error - Try Again';
        setTimeout(function () { btn.textContent = 'Add All to Cart'; }, 3000);
      });
  }

  // ---- URL State (share/restore) ----

  function encodeState() {
    var parts = [];
    DATA.categories.forEach(function (cat) {
      var s = state[cat.id];
      if (s.state === 'skip') return;
      var code = cat.id + ':' + (s.state === 'need' ? 'n' : 'h') + ':' + s.sizeIdx;
      if (s.backwashing) code += ':bw';
      parts.push(code);
    });
    if (lowPh) parts.push('lowph:1');
    return parts.join(',');
  }

  function decodeState(str) {
    if (!str) return false;
    var parts = str.split(',');
    var found = false;
    parts.forEach(function (part) {
      var segs = part.split(':');
      if (segs[0] === 'lowph') {
        lowPh = segs[1] === '1';
        return;
      }
      if (segs.length < 3) return;
      var catId = segs[0];
      var st = segs[1] === 'h' ? 'have' : 'need';
      var idx = parseInt(segs[2], 10) || 0;
      var bw = segs.indexOf('bw') !== -1;
      if (state[catId]) {
        state[catId] = { state: st, sizeIdx: idx, backwashing: bw };
        found = true;
      }
    });
    return found;
  }

  function getShareUrl() {
    var url = window.location.origin + window.location.pathname;
    var config = encodeState();
    if (config) url += '?config=' + encodeURIComponent(config);
    return url;
  }

  function updateUrlState() {
    var config = encodeState();
    if (config && window.history && window.history.replaceState) {
      var params = new URLSearchParams();
      params.set('config', config);
      var ret = new URLSearchParams(window.location.search).get('return');
      if (ret) params.set('return', ret);
      window.history.replaceState(null, '', window.location.pathname + '?' + params.toString());
    }
  }

  function triggerShare() {
    var url = getShareUrl();
    var title = 'My Water Treatment System Plan';
    var text = 'Here is my custom water treatment system plan from Mid Atlantic Water.';

    if (navigator.share) {
      navigator.share({ title: title, text: text, url: url }).catch(function () {});
      return;
    }

    var popup = document.getElementById('sb-share-popup');
    var linkInput = document.getElementById('sb-share-link');
    var emailLink = document.getElementById('sb-share-email');
    var smsLink = document.getElementById('sb-share-sms');

    linkInput.value = url;
    emailLink.href = 'mailto:?subject=' + encodeURIComponent(title) + '&body=' + encodeURIComponent(text + '\n\n' + url);
    smsLink.href = 'sms:?body=' + encodeURIComponent(text + ' ' + url);
    popup.style.display = 'flex';
  }

  function initShare() {
    var btns = document.querySelectorAll('#sb-share-btn, #sb-share-btn-nav');
    for (var i = 0; i < btns.length; i++) {
      btns[i].addEventListener('click', triggerShare);
    }

    var copyBtn = document.getElementById('sb-share-copy');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var linkInput = document.getElementById('sb-share-link');
        linkInput.select();
        if (navigator.clipboard) {
          navigator.clipboard.writeText(linkInput.value).then(function () {
            copyBtn.textContent = 'Copied!';
            setTimeout(function () { copyBtn.textContent = 'Copy Link'; }, 2000);
          });
        } else {
          document.execCommand('copy');
          copyBtn.textContent = 'Copied!';
          setTimeout(function () { copyBtn.textContent = 'Copy Link'; }, 2000);
        }
      });
    }

    var closeBtn = document.getElementById('sb-share-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        document.getElementById('sb-share-popup').style.display = 'none';
      });
    }

    var popup = document.getElementById('sb-share-popup');
    if (popup) {
      popup.addEventListener('click', function (e) {
        if (e.target === popup) popup.style.display = 'none';
      });
    }
  }

  // ---- Init ----

  function init() {
    var step1 = document.getElementById('sb-step1-list');
    if (step1) step1.innerHTML = '<div style="text-align:center;padding:32px;color:#999;">Loading products...</div>';

    fetchAllProducts(function () {
      var params = new URLSearchParams(window.location.search);
      var config = params.get('config');
      var restored = decodeState(config);

      // Source of truth precedence (highest wins):
      //   1. ?config=...   -- shared link, user explicitly opened a saved plan
      //   2. localStorage maw_builder_state -- the user's own in-progress plan
      //   3. localStorage maw_water_profile -- the user told the collection sizer
      //      what their water looks like; pre-select systems based on that
      //   4. Default empty state (everything 'skip')
      if (!restored) {
        var savedBuilder = loadJSON(BUILDER_STATE_KEY);
        if (savedBuilder && applyBuilderStateSnapshot(savedBuilder)) {
          restored = true;
          maxStepReached = 2;
        } else {
          var profile = loadJSON(WATER_PROFILE_KEY);
          if (profile && applyWaterProfileToState(profile)) {
            // Pre-filled from water profile: jump to sizing step so the
            // customer sees the recommendation immediately
            maxStepReached = 2;
            saveBuilderState();
          }
        }
      }

      if (restored) {
        maxStepReached = 3;
        goToStep(3);
      } else if (getActiveCats().length > 0) {
        goToStep(2);
      } else {
        renderStep1();
        updateProgress();
      }

      initProgressNav();
      initProgressLabels();

      document.getElementById('sb-next-1').addEventListener('click', function () { goToStep(2); });
      document.getElementById('sb-next-2').addEventListener('click', function () { goToStep(3); });
      document.getElementById('sb-back-2').addEventListener('click', function () { goToStep(1); });
      document.getElementById('sb-back-3').addEventListener('click', function () { goToStep(2); });
      document.getElementById('sb-restart').addEventListener('click', function () {
        DATA.categories.forEach(function (cat) {
          state[cat.id] = { state: 'skip', sizeIdx: cat.defaultSizeIdx || 0, backwashing: false };
        });
        lowPh = false;
        maxStepReached = 1;
        clearKey(BUILDER_STATE_KEY);
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', window.location.pathname);
        }
        goToStep(1);
      });

      var addBtn = document.getElementById('sb-add-to-cart');
      if (addBtn) addBtn.addEventListener('click', handleAddToCart);

      var saveBtn = document.getElementById('sb-save-diagram');
      if (saveBtn) saveBtn.addEventListener('click', saveDiagram);

      initShare();

      window.addEventListener('scroll', function () {
        if (document.getElementById('sb-step-2').style.display !== 'none') {
          updateScrollHint();
        }
      }, { passive: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
